import { JSX, FC, useState, Key, useEffect, SetStateAction, Dispatch } from "react";
import axios from "axios";
import useCancelableDebounce from "hooks/useCancelableDebounce";
import { useForm, useWatch } from "antd/es/form/Form";
import { useLocation } from "react-router";

// utils
import { handleError } from "utils/handleError";
import generateSorter from "utils/generateSorter";
import { formatDateRange } from "utils/formatDateRange";
import { transformSearchParamsToObject } from "utils/searchParamsToObject";
import { buildSearchParams } from "utils/buildSearchParams";

// redux
import { useDispatch, useSelector } from "react-redux";
import { AppStateType } from "redux/reducers/mainReducer";
import { AppDispatch } from "redux/store/store";
import { deleteAllProducts, deleteProduct, fetchProducts } from "redux/thunks/products.thunk";

//components
import { FormInstance, notification } from "antd";
import UploadedProductsFiltersBlock from "./UploadedProductsFiltersBlock/UploadedProductsFiltersBlock";
import UploadedProductsModals from "./UploadedProductsModals/UploadedProductsModals";
import UploadedProductsTableBlock from "./UploadedProductsTableBlock/UploadedProductsTableBlock";

// types
import { SorterResult, TablePaginationConfig } from "antd/es/table/interface";
import { ProductType, ProductsRequestType, } from "products/types";
import { ProductsFiltersValuesType } from "app/types";

interface IUploadedProductsTabProps {
  form: FormInstance;
  setActiveTab: Dispatch<SetStateAction<string>>;
}

const UploadedProductsTab: FC<IUploadedProductsTabProps> = ({ setActiveTab }): JSX.Element => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();

  const [form] = useForm<ProductsFiltersValuesType>();
  const values: ProductsFiltersValuesType = useWatch<FormInstance>([], form);

  const {
    list,
    count,
    minPrice,
    maxPrice,
  } = useSelector((state: AppStateType) => state.productsSlice.products);

  // фильтры, сортировки, пагинация, поиск
  const [previousFilters, setPreviousFilters] = useState<ProductsFiltersValuesType>({
    categoryBK: [],
    date: null,
    draft: false,
    error: false,
    price: [minPrice, maxPrice],
    published: false,
    supplierCategory: []
  });
  const [paginationInfo, setPaginationInfo] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    locale: {
      items_per_page: "/ на странице",
    },
  });
  const [params, setParams] = useState<URLSearchParams | null>(() => new URLSearchParams(location.search));
  const [sorterInfo, setSorterInfo] = useState<SorterResult<any> | SorterResult<any>[]>(null);
  const [searchValue, setSearchValue] = useState<string>(null);
  const [isFiltersConfirmed, setIsFiltersConfirmed] = useState<boolean>(false);

  const [productsToDelete, setProductsToDelete] = useState<ProductType[]>([]);
  const [isShowDeleteModal, setIsShowDeleteModal] = useState<boolean>(false);
  const [isShowCreateCopyModal, setIsShowCreateCopyModal] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<ProductType[]>(null);
  const [selectedKeys, setSelectedKeys] = useState<Key[]>(null);
  const [countProductsForDelete, setCountProductsForDelete] = useState<number>(0);
  const [excludedProductIds, setExcludedProductIds] = useState<number[]>([]);
  const [isAllProductsSelected, setIsAllProductsSelected] = useState<boolean>(false);
  const [isSingleProductDelete, setIsSingleProductDelete] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [confirmText, setConfirmText] = useState<string>("");
  const [isTagFilterDeleted, setIsTagFilterDeleted] = useState<boolean>(false);

  useEffect(() => {
    if (isAllProductsSelected) {
      setCountProductsForDelete(count - excludedProductIds?.length);
      setSelectedRows(list?.map((product: ProductType, idx) => ({ key: idx.toString(), ...product }))
        ?.filter((product: ProductType) => !excludedProductIds?.includes(product.id))
      );
    }
  }, [isAllProductsSelected, list, excludedProductIds]);

  useEffect(() => {
    fetchProductsList({
      page: params.get("page") ?? paginationInfo.current,
      page_size: params.get("page_size") ?? paginationInfo.pageSize,
      search: params.get("search") ?? searchValue,
      ...(values ? getRequestFilters(previousFilters) : {}),
      ...(sorterInfo ? { order_by: generateSorter(sorterInfo) } : {}),
      ...transformSearchParamsToObject(params),
    });
  }, [params, sorterInfo]);

  useEffect(() => {
    // в url записываем актуальную пагинацию и поиск
    setParams(buildSearchParams({
      ...transformSearchParamsToObject(params),
      page: paginationInfo.current,
      page_size: paginationInfo.pageSize,
      ...(searchValue ? { search: searchValue } : {})
    }));

    !isAllProductsSelected && setSelectedRows(null);
  }, [paginationInfo.current, paginationInfo.pageSize, searchValue, sorterInfo]);

  useEffect(() => {
    if (isFiltersConfirmed) {
      const filters: ProductsRequestType = getRequestPaginationWithFilters(!isTagFilterDeleted
        ? values
        : previousFilters
      );

      const newParams: URLSearchParams = buildSearchParams(filters);

      setIsFiltersConfirmed(false);
      setParams(newParams);
      fetchProductsList(filters);
    }
  }, [isFiltersConfirmed]);

  useEffect(() => {
    if (params !== undefined && params !== null) {
      const queryString: string = params?.toString();
      const newUrl: string = queryString ? `${location.pathname}?${queryString}` : location.pathname;

      window.history.pushState(null, "", newUrl);
    }
  }, [params, location.pathname]);

  useEffect(() => {
    setSelectedKeys(selectedRows?.map((value) => value.key));
  }, [selectedRows]);

  useEffect(() => {
    if (!isShowDeleteModal) {
      setIsSingleProductDelete(false);
    }
  }, [isShowDeleteModal]);

  const { debouncedFunction: fetchProductsList } = useCancelableDebounce((params, token) => {
    const searchParams: ProductsRequestType = {
      page: paginationInfo.current,
      page_size: paginationInfo.pageSize,
    };

    dispatch(fetchProducts(params || searchParams, token))
      .catch((error: any) => {
        if (!axios.isCancel(error)) {
          handleError("Ошибка при загрузке списка товаров.", error);
        }
      });
  });

  const getRequestPaginationWithFilters = (filters: ProductsFiltersValuesType): ProductsRequestType => ({
    page: 1,
    page_size: paginationInfo.pageSize,
    ...(searchValue ? { search: searchValue } : {}),
    ...getRequestFilters(filters),
    ...(sorterInfo ? { order_by: generateSorter(sorterInfo) } : {}),
  });

  const getRequestFilters = (filters: ProductsFiltersValuesType): ProductsRequestType => {
    const statuses: string[] = Object.entries({
      "Размещен": filters.published,
      "Ошибка": filters.error,
      "Черновик": filters.draft,
    })
      .filter(([, isActive]) => isActive)
      .map(([status]) => status);

    const categorySupplierIds: number[] = filters?.supplierCategory?.map(({ value }) => (value as number));
    const categoryTpIds: number[] = filters?.categoryBK?.map(({ value }) => (value as number));
    const needSendPrice: boolean = filters.price?.[0] !== minPrice || filters.price?.[1] !== maxPrice;

    return {
      ...(needSendPrice && filters.price?.[0] ? { price_from: filters.price[0] } : {}),
      ...(needSendPrice && filters.price?.[1] ? { price_to: filters.price[1] } : {}),
      ...(formatDateRange(["updated_at_from", "updated_at_to"], filters.date)),
      ...(statuses.length ? { status: statuses } : {}),
      ...(filters.supplierCategory?.length ? { category_supplier: categorySupplierIds } : {}),
      ...(filters.categoryBK?.length ? { category_bk: categoryTpIds } : {})
    };
  };

  const showDeleteModal = (values: ProductType[], text: string): void => {
    setConfirmText(text);
    setProductsToDelete(values);
    setIsShowDeleteModal(true);
  };

  const closeDeleteModal = (): void => setIsShowDeleteModal(false);

  const handleSuccessDelete = (description: string): void => {
    notification.success({ message: "Товары успешно удалены", description });

    fetchProductsList(getRequestPaginationWithFilters(previousFilters));
  };

  const handleFailureDelete = (error: any): void => {
    handleError(
      "При удалении товара(ов) произошла ошибка, пожалуйста, повторите позже.",
      error
    );
  };

  const clearSelectedProducts = (): void => {
    setSelectedRows(null);
    setProductsToDelete([]);
    setCountProductsForDelete(0);
  };

  const deleteProducts = (): void => {
    setIsLoading(true);
    closeDeleteModal();

    const ids: number[] = productsToDelete.map(({ id }) => id);

    dispatch(deleteProduct(ids))
      .then(() => {
        handleSuccessDelete(`${isSingleProductDelete ? "1" : countProductsForDelete} шт.`);

        if (!isAllProductsSelected) {
          clearSelectedProducts();
        }
      })
      .catch((error) => handleFailureDelete(error))
      .finally(() => setIsLoading(false));
  };

  const deleteAllProductsList = (): void => {
    setIsLoading(true);
    closeDeleteModal();

    dispatch(deleteAllProducts({
      ...(excludedProductIds?.length ? { exclude_ids: excludedProductIds } : {}),
      ...getRequestFilters(previousFilters),
      ...(searchValue ? { search: searchValue } : {}),
    }))
      .then(({ data }) => {
        if (data?.failed) {
          notification.warning({
            message: `По техническим причинам ${data?.failed} товаров удалить не удалось. 
              Повторите попытку позже или обратитесь в службе тех. поддержки по телефону 8(800)222-42-14 
              или по адресу help@vtbconnect.ru`
          });
        }

        handleSuccessDelete(`${data?.failed ? (countProductsForDelete - data?.failed) : countProductsForDelete} шт.`);
        setIsAllProductsSelected(false);
        setExcludedProductIds([]);
        clearSelectedProducts();
      })
      .catch((error) => handleFailureDelete(error))
      .finally(() => setIsLoading(false));
  };

  const showCreateCopyModal = (): void => setIsShowCreateCopyModal(true);

  const closeCreateCopyModal = (): void => setIsShowCreateCopyModal(false);

  return (
    <div>
      <UploadedProductsFiltersBlock
        form={form}
        values={values}
        params={params}
        isLoading={isLoading}
        selectedRows={selectedRows}
        setSearchValue={setSearchValue}
        showDeleteModal={showDeleteModal}
        previousFilters={previousFilters}
        setPreviousFilters={setPreviousFilters}
        isShowDeleteButton={isAllProductsSelected || !!selectedRows?.length}
        isFiltersConfirmed={isFiltersConfirmed}
        isTagFilterDeleted={isTagFilterDeleted}
        setIsTagFilterDeleted={setIsTagFilterDeleted}
        setIsFiltersConfirmed={setIsFiltersConfirmed}
        countProductsForDelete={countProductsForDelete}
      />
      <UploadedProductsTableBlock
        selectedKeys={selectedKeys}
        selectedRows={selectedRows}
        setActiveTab={setActiveTab}
        setSorterInfo={setSorterInfo}
        setSelectedKeys={setSelectedKeys}
        setSelectedRows={setSelectedRows}
        setPaginationInfo={setPaginationInfo}
        showDeleteModal={showDeleteModal}
        showCreateCopyModal={showCreateCopyModal}
        isAllProductsSelected={isAllProductsSelected}
        setExcludedProductIds={setExcludedProductIds}
        setIsSingleProductDelete={setIsSingleProductDelete}
        setIsAllProductsSelected={setIsAllProductsSelected}
        setCountProductsForDelete={setCountProductsForDelete}
      />
      <UploadedProductsModals
        isLoading={isLoading}
        isShowDeleteModal={isShowDeleteModal}
        isShowCreateCopyModal={isShowCreateCopyModal}
        confirmText={confirmText}
        onOkDeleteModal={(isAllProductsSelected && !isSingleProductDelete) ? deleteAllProductsList : deleteProducts}
        closeDeleteModal={closeDeleteModal}
        closeCreateCopyModal={closeCreateCopyModal}
      />
    </div>
  );
};

export default UploadedProductsTab;
